import styled from '@emotion/styled';
import { UUID } from '@myme/utils';
import { BinIcon } from 'src/icons';
import { usePalette, useTranslation } from 'src/hooks';
import { SvgIcon, Typography } from 'src/components/atoms';
import { FlexColumn } from 'src/components/layout';
import { Dialog } from 'src/components/organisms';
import { useDeleteMediaFile, useHandleDeleteMediaFileConsequencesQuery } from './useDeleteMediaFile';

interface Props {
  fileUUID: UUID;
  onClose: () => void;
}

const StyledBinIconContainer = styled(SvgIcon)`
  margin-bottom: 1rem;
  height: 4rem;
  width: 4rem;
`;

export const DeleteSingleFileDialog = ({ fileUUID, onClose }: Props) => {
  const { t } = useTranslation();
  const { error } = usePalette();
  const deleteMediaConsequencesQuery = useHandleDeleteMediaFileConsequencesQuery(fileUUID);
  const { handleDeleteMediaFile } = useDeleteMediaFile(fileUUID);

  const handleConfirmDelete = () => {
    handleDeleteMediaFile.mutate();
    onClose();
  };

  if (!deleteMediaConsequencesQuery.isSuccess) {
    return null;
  }

  return (
    <Dialog
      title={t('pages.mymes.editor.blobs.library.media_delete_dialog_title')}
      onClose={onClose}
      defaultActionButtons={{
        action: handleConfirmDelete,
        btnConfirmText: t('pages.mymes.editor.blobs.library.media_delete_dialog_btn'),
        otherActionBtnProps: { color: 'error', sx: { backgroundColor: error.main } }
      }}
      enableBackdropClick
      enableEscapeKeyDown
    >
      <FlexColumn alignItems='center'>
        <StyledBinIconContainer>
          <BinIcon stroke={error.main} fill='transparent' />
        </StyledBinIconContainer>
        <Typography textAlign='center'>{t('pages.mymes.editor.blobs.library.media_delete_dialog_text')}</Typography>

        {Boolean(deleteMediaConsequencesQuery.data.mymes.length) && (
          <FlexColumn mt={4} textAlign='center'>
            <Typography variant='body2'>{t('pages.mymes.editor.blobs.library.media_delete_consequences')}</Typography>
            {deleteMediaConsequencesQuery.data.mymes.map(mymeName => (
              <Typography key={mymeName} variant='body2' sx={{ mt: 1 }}>
                {`- ${mymeName}`}
              </Typography>
            ))}
          </FlexColumn>
        )}
      </FlexColumn>
    </Dialog>
  );
};
